import React, { useState, useEffect } from "react";
import Moment from "moment";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import SwipeableViews from "react-swipeable-views";
import MobileStepper from "@mui/material/MobileStepper";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";

import Languages from "../../../translations";

const BG_COLOR = "#fbcece";

const DescriptionOfChildDetail = ({
  SelectedSlideIndex,
  rows,
  sectionTitle,
}) => {
  const slides = [
    {
      title: Languages.ChildStrengthsAndIterests,
      entryField: "ChildsStrengthsAndIntrests",
    },
    {
      title: Languages.ChildWellbeingAndLearningObjectives,
      entryField: "ChildWellBeingAndLearningGoals",
    },
    {
      title: Languages.OtherImportanInfo,
      entryField: "OtherImportantInformation",
    },
  ];

  const MaxSlideCount = slides.length;
  const [SlideIndex, setSlideIndex] = useState(SelectedSlideIndex);

  useEffect(() => {
    setSlideIndex(SelectedSlideIndex);
  }, [SelectedSlideIndex]);

  const handleChangeIndex = (index) => setSlideIndex(index);
  const handlePrev = () => {
    if (SlideIndex > 0) setSlideIndex(SlideIndex - 1);
  };
  const handleNext = () => {
    if (SlideIndex < MaxSlideCount - 1) setSlideIndex(SlideIndex + 1);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "READY":
        return "Valmis";
      case "DRAFT":
        return "Luonnos";
      case "UNDEFINED":
      default:
        return null;
    }
  };

  if (!rows || rows.length === 0) return null;

  const currentSlide = slides[SlideIndex];
  const currentEntryField = currentSlide?.entryField;

  const renderReadonlyBox = (key, entry, statusLabel, evaluatedDate) => {
    const text = entry?.Text;
    const author = entry?.PersonName;
    const saved = entry?.Saved
      ? Moment(entry.Saved).format("DD.MM.YYYY HH:mm")
      : "";

    if (!text) return null;

    return (
      <Box
        key={key}
        sx={{
          backgroundColor: BG_COLOR,
          borderRadius: 2,
          boxShadow: 1,
          mt: 1.5,
          pt: 1.5,
          pb: 1.5,
          px: 1,
        }}
      >
        {(author || saved || statusLabel || evaluatedDate) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
              ml: 1,
              mr: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {author ? (
                <Typography
                  sx={{ fontWeight: "bold", fontSize: 13, color: "#424242" }}
                >
                  {author}
                </Typography>
              ) : null}
              {!author && statusLabel ? (
                <Chip label={statusLabel} size="small" variant="outlined" />
              ) : null}
            </Box>
            {(saved || evaluatedDate) && (
              <Typography variant="caption" color="text.secondary">
                {saved || Moment(evaluatedDate).format("DD.MM.YYYY")}
              </Typography>
            )}
          </Box>
        )}
        <Typography
          variant="body1"
          sx={{
            color: "black",
            ml: 1,
            mb: 1,
            whiteSpace: "pre-line",
            overflowWrap: "break-word",
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <SwipeableViews
        className={"col-12 textCenter slide pt-3"}
        index={SlideIndex}
        key={SlideIndex}
        onChangeIndex={handleChangeIndex}
      >
        {slides.map((slide, index) => (
          <div key={index}>
            <Box className="br-top col-12" sx={{ backgroundColor: BG_COLOR }}>
              <Typography
                color="textSecondary"
                sx={{
                  textAlign: "center",
                  fontWeight: "Bold",
                  fontSize: 18,
                  color: "#424242",
                  pt: 1,
                }}
              >
                {sectionTitle}
              </Typography>
              <MobileStepper
                style={{
                  backgroundColor: BG_COLOR,
                  color: "black",
                  padding: 0,
                }}
                variant="text"
                steps={MaxSlideCount}
                position="static"
                activeStep={SlideIndex}
                nextButton={
                  <Button
                    size="small"
                    style={{
                      color: "black",
                      opacity: SlideIndex === MaxSlideCount - 1 ? "0.5" : "",
                    }}
                    onClick={handleNext}
                    disabled={SlideIndex === MaxSlideCount - 1}
                  >
                    {Languages.next} <KeyboardArrowRight />
                  </Button>
                }
                backButton={
                  <Button
                    size="small"
                    style={{
                      color: "black",
                      opacity: SlideIndex === 0 ? "0.5" : "",
                    }}
                    onClick={handlePrev}
                    disabled={SlideIndex === 0}
                  >
                    <KeyboardArrowLeft /> {Languages.prev}
                  </Button>
                }
              />
            </Box>
            <Box
              className="br-btm col-12"
              sx={{ boxShadow: 2, mb: 3, pb: 1, backgroundColor: "white" }}
            >
              <Typography
                color="textSecondary"
                sx={{
                  textAlign: "center",
                  fontWeight: "Medium",
                  fontSize: 16,
                  color: "#424242",
                  pt: 1,
                }}
              >
                {slide.title}
              </Typography>
            </Box>
          </div>
        ))}
      </SwipeableViews>

      <Box sx={{ px: 1 }}>
        <Divider textAlign="left">
          <Chip
            label={
              <Typography
                sx={{ fontWeight: "bold", fontSize: 14, color: "#424242" }}
              >
                {Languages.StaffNotes}
              </Typography>
            }
            variant="outlined"
          />
        </Divider>

        {rows.map((row, idx) => {
          const entry = row[currentEntryField];
          const statusLabel = getStatusLabel(row.Status);

          if (!entry?.Text) return null;

          return renderReadonlyBox(idx, entry, statusLabel, row.EvaluatedDate);
        })}

        {rows.every((row) => !row[currentEntryField]?.Text) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, textAlign: "center" }}
          >
            {Languages.guideNoContent}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default DescriptionOfChildDetail;
